import {
    getMediaPhotoIdentity,
    getTrackIdentity,
} from "../../../../utils/profileEditorial";
import {
    normalizeBandRouteHandle,
} from "../../../../utils/bandRoutes";
import { isActiveBandMemberEntry } from "../../../../utils/bandMembership";
import { MIN_FOUNDED_YEAR } from "../constants";

export const getBandIdentity = (band) => String(band?.id || band?.objectId || "");

export const extractBandAvatarUrl = (band) => {
    const avatar = band?.images?.avatar;
    if (typeof avatar === "string") {
        return avatar.trim();
    }
    if (avatar && typeof avatar === "object" && typeof avatar.url === "string") {
        return avatar.url.trim();
    }
    return "";
};

export const getBandImageObjectKeyFromUrl = (url) => {
    if (typeof url !== "string" || !url.trim()) {
        return "";
    }
    try {
        const parsed = new URL(url, "https://muzone.local");
        return parsed.searchParams.get("objectKey") || "";
    } catch (_error) {
        return "";
    }
};

const buildBandImageReference = (currentImage, imageUrl, reference = {}) => {
    const nextUrl = imageUrl.trim();
    const explicitObjectKey = typeof reference?.objectKey === "string" && reference.objectKey.trim()
        ? reference.objectKey.trim()
        : null;
    const explicitUpdatedAt = typeof reference?.updatedAt === "string" && reference.updatedAt.trim()
        ? reference.updatedAt.trim()
        : null;
    return {
        objectKey: explicitObjectKey || getBandImageObjectKeyFromUrl(nextUrl) || currentImage?.objectKey || null,
        url: nextUrl,
        updatedAt: explicitUpdatedAt || new Date().toISOString(),
    };
};

export const mergeBandCoverImage = (band, imageUrl, reference) => {
    if (!band || typeof imageUrl !== "string" || !imageUrl.trim()) {
        return band;
    }
    const nextUrl = imageUrl.trim();
    const images = band.images && typeof band.images === "object" ? band.images : {};
    const currentCover = images.cover && typeof images.cover === "object" ? images.cover : {};
    const imageReference = buildBandImageReference(currentCover, nextUrl, reference);
    return {
        ...band,
        coverImageUrl: nextUrl,
        images: {
            ...images,
            cover: {
                ...currentCover,
                ...imageReference,
            },
        },
    };
};

export const mergeBandAvatarImage = (band, imageUrl, reference) => {
    if (!band || typeof imageUrl !== "string" || !imageUrl.trim()) {
        return band;
    }
    const nextUrl = imageUrl.trim();
    const images = band.images && typeof band.images === "object" ? band.images : {};
    const currentAvatar = images.avatar && typeof images.avatar === "object" ? images.avatar : {};
    const imageReference = buildBandImageReference(currentAvatar, nextUrl, reference);
    return {
        ...band,
        avatarUrl: nextUrl,
        iconUrl: nextUrl,
        images: {
            ...images,
            avatar: {
                ...currentAvatar,
                ...imageReference,
            },
        },
    };
};

export const formatCreatedYear = (createdAt) => {
    if (!createdAt) return null;
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return null;
    return date.getFullYear();
};

export const normalizeFoundedYearDraft = (value, fallbackYear = null) => {
    const raw = value == null ? "" : String(value).trim();
    const candidate = raw || (fallbackYear == null ? "" : String(fallbackYear));
    if (!/^\d{4}$/.test(candidate)) {
        throw new Error("Founded year must be a four-digit year.");
    }
    const year = Number.parseInt(candidate, 10);
    const currentYear = new Date().getFullYear();
    if (year < MIN_FOUNDED_YEAR || year > currentYear) {
        throw new Error(`Founded year must be between ${MIN_FOUNDED_YEAR} and ${currentYear}.`);
    }
    return year;
};

export const isPublicBand = (band) => String(band?.visibility || "").trim().toUpperCase() === "PUBLIC";

export const mergeBandGalleryPhotos = (primaryPhotos, secondaryPhotos) => {
    const merged = [];
    const seen = new Set();
    [primaryPhotos, secondaryPhotos].forEach((photos) => {
        (Array.isArray(photos) ? photos : []).forEach((photo) => {
            if (!photo) return;
            const identity = getMediaPhotoIdentity(photo);
            if (identity) {
                if (seen.has(identity)) return;
                seen.add(identity);
            }
            merged.push(photo);
        });
    });
    return merged;
};

export const isBandGalleryPhotoForBand = (photo, bandId) => {
    if (!photo || !bandId) {
        return false;
    }
    const normalizedBandId = String(bandId);
    if (photo.bandId && String(photo.bandId) === normalizedBandId) {
        return true;
    }
    const objectKey = typeof photo.objectKey === "string" ? photo.objectKey : "";
    return objectKey.startsWith(`band-media-photos/${normalizedBandId}/`);
};

export const replaceBandGalleryPhotos = (currentPhotos, bandId, nextBandPhotos) => {
    const otherPhotos = (Array.isArray(currentPhotos) ? currentPhotos : [])
        .filter((photo) => !isBandGalleryPhotoForBand(photo, bandId));
    return mergeBandGalleryPhotos(nextBandPhotos, otherPhotos);
};

export const mergeBandTracks = (primaryTracks, secondaryTracks) => {
    const merged = [];
    const seen = new Set();
    [primaryTracks, secondaryTracks].forEach((tracks) => {
        (Array.isArray(tracks) ? tracks : []).forEach((track) => {
            if (!track) return;
            const identity = getTrackIdentity(track);
            if (identity) {
                if (seen.has(identity)) return;
                seen.add(identity);
            }
            merged.push(track);
        });
    });
    return merged;
};

const normalizeText = (value) => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    return trimmed || null;
};

const normalizeNumber = (value) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    return value;
};

const withoutEmptyLocationFields = (location) => Object.fromEntries(
    Object.entries(location).filter(([, entryValue]) => entryValue !== null && entryValue !== undefined),
);

const normalizeLocationComparisonValue = (value) => {
    const normalized = normalizeText(value);
    return normalized ? normalized.replace(/\s+/g, " ").toLowerCase() : null;
};

const buildStructuredLocationDisplayCandidates = (band) => {
    const city = normalizeText(band?.city);
    const stateOrRegion = normalizeText(band?.stateOrRegion);
    const country = normalizeText(band?.country);
    const parts = [city, stateOrRegion, country].filter(Boolean);
    return [
        normalizeText(band?.locationDisplay),
        parts.join(", "),
        city && stateOrRegion ? `${city}, ${stateOrRegion}` : null,
        city && country ? `${city}, ${country}` : null,
        city && stateOrRegion && country ? `${city}, ${stateOrRegion}, ${country}` : null,
    ]
        .map(normalizeLocationComparisonValue)
        .filter(Boolean);
};

const locationDisplayMatchesStructuredLocation = (band, locationDisplay) => {
    const next = normalizeLocationComparisonValue(locationDisplay);
    if (!next) return false;
    return buildStructuredLocationDisplayCandidates(band).includes(next);
};

export const buildLocationDisplayUpdatePayload = (band, value) => {
    const locationDisplay = normalizeText(value);
    if (!locationDisplay) {
        return { location: null, locationDisplay: null };
    }
    if (!locationDisplayMatchesStructuredLocation(band, locationDisplay)) {
        return {
            location: {
                formatted: locationDisplay,
                source: "MANUAL",
            },
            locationDisplay,
        };
    }
    return {
        location: withoutEmptyLocationFields({
            city: normalizeText(band?.city),
            stateOrRegion: normalizeText(band?.stateOrRegion),
            country: normalizeText(band?.country),
            countryCode: normalizeText(band?.countryCode),
            latitude: normalizeNumber(band?.coordinateLat),
            longitude: normalizeNumber(band?.coordinateLng),
            formatted: locationDisplay,
            source: "MANUAL",
        }),
        locationDisplay,
    };
};

export const toBandPageBand = (band, fallback = null) => {
    if (!band && !fallback) {
        return null;
    }
    const merged = { ...(fallback || {}), ...(band || {}) };
    if (!Array.isArray(merged.members) && Array.isArray(fallback?.members)) {
        merged.members = fallback.members;
    }
    return {
        ...merged,
        iconUrl: extractBandAvatarUrl(band) || merged.iconUrl || "",
        currentUserIsOwner: Boolean(band?.currentUserIsOwner),
        currentUserIsMember: Boolean(band?.currentUserIsMember),
        currentUserIsFollowing: Boolean(merged.currentUserIsFollowing),
        currentUserMembershipStatus: band?.currentUserMembershipStatus || null,
    };
};

export const buildRouteBandLookupKey = (locator) => {
    if (!locator) {
        return null;
    }
    if (locator.type === "handle") {
        const handle = normalizeBandRouteHandle(locator.value);
        return handle ? `handle:${handle}` : null;
    }
    return null;
};

export const selectedBandMatchesRouteLocator = (band, locator) => {
    if (!band || !locator) {
        return false;
    }
    if (locator.type === "handle") {
        return normalizeBandRouteHandle(band.handle) === normalizeBandRouteHandle(locator.value);
    }
    return false;
};

export const mergeUpdatedBandMember = (band, memberUserId, updatedMember, selectedInstrument) => {
    if (!band || !memberUserId || !Array.isArray(band.members)) {
        return band;
    }
    const normalizedMemberUserId = String(memberUserId);
    let didUpdate = false;
    const nextMembers = band.members.map((member) => {
        if (!member || String(member.userId || "") !== normalizedMemberUserId) {
            return member;
        }
        didUpdate = true;
        const hasUpdatedUserInstrument = Object.prototype.hasOwnProperty.call(updatedMember?.user || {}, "mainInstrument");
        const hasUpdatedMemberInstrument = Object.prototype.hasOwnProperty.call(updatedMember || {}, "mainInstrument");
        const nextMainInstrument = hasUpdatedUserInstrument
            ? updatedMember.user.mainInstrument
            : (hasUpdatedMemberInstrument ? updatedMember.mainInstrument : selectedInstrument);
        return {
            ...member,
            ...(updatedMember || {}),
            userId: member.userId,
            user: {
                ...(member.user || {}),
                ...(updatedMember?.user || {}),
                mainInstrument: nextMainInstrument ?? null,
            },
        };
    });
    return didUpdate ? { ...band, members: nextMembers } : band;
};

const isPendingBandMemberEntry = (member) => {
    const status = String(member?.membershipStatus || member?.status || "").trim().toUpperCase();
    return member?.isPending === true || status === "PENDING" || status === "INVITED";
};

export const removeBandMemberFromBand = (band, memberUserId, { removedMemberIsPending = false } = {}) => {
    if (!band || !memberUserId || !Array.isArray(band.members)) {
        return band;
    }
    const normalizedMemberUserId = String(memberUserId);
    let removedMember = null;
    let didRemove = false;
    const nextMembers = band.members.filter((member) => {
        const currentUserId = member?.userId;
        const shouldKeep = String(currentUserId || "") !== normalizedMemberUserId;
        if (!shouldKeep) {
            removedMember = member;
            didRemove = true;
        }
        return shouldKeep;
    });
    if (!didRemove) {
        return band;
    }
    const decrementCount = (value) => (
        Number.isFinite(value) ? Math.max(0, value - 1) : value
    );
    const shouldDecrementCounts = !(removedMemberIsPending || isPendingBandMemberEntry(removedMember));
    const removedRole = String(removedMember?.membershipRole || removedMember?.role || "").trim().toUpperCase();
    const shouldPromoteSuccessor = removedRole === "OWNER";
    const successorIndex = shouldPromoteSuccessor
        ? nextMembers.findIndex((member) => isActiveBandMemberEntry(member, { treatMissingAsActive: true }))
        : -1;
    const promotedMembers = successorIndex >= 0
        ? nextMembers.map((member, index) => (
            index === successorIndex
                ? { ...member, role: "OWNER", membershipRole: "OWNER" }
                : member
        ))
        : nextMembers;
    const successor = successorIndex >= 0 ? promotedMembers[successorIndex] : null;
    return {
        ...band,
        members: promotedMembers,
        memberCount: shouldDecrementCounts ? decrementCount(band.memberCount) : band.memberCount,
        activeMemberCount: shouldDecrementCounts ? decrementCount(band.activeMemberCount) : band.activeMemberCount,
        ownerId: successor?.userId || band.ownerId,
        owner: successor || band.owner,
    };
};
